import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    /**
     * The base URL for the API (version 2)
     *  - Used in all functions which make API calls ([getRoomToken]{@link ApiService#getRoomToken}, [getRoomDetails]{@link ApiService#getRoomDetails}, [getRoomConfig]{@link ApiService#getRoomConfig}, [updateParticipant]{@link ApiService#updateParticipant})
     */
    apiBaseUrl: string = `${environment.apiUrl}/v2/`;

    /**
     * The token used to authenticate the user
     */
    private _token: any;

    private static readonly TOKEN_KEY = 'vpm_api_token';

    /**
     * Gets the token used to authenticate the user
     *  - Used as a check in the [headers]{@link ApiService#headers} getter when setting the headers
     */
    public get token(): string {
        return (
            this._token || sessionStorage.getItem(ApiService.TOKEN_KEY) || ''
        );
    }

    /**
     * Sets the token used to authenticate the user
     *  - Set in [getRoomToken]{@link WelcomeComponent#getRoomToken}
     */
    public set token(value: string) {
        this._token = value;
        if (value) {
            sessionStorage.setItem(ApiService.TOKEN_KEY, value);
        }
    }

    /**
     * The headers used in the API calls
     *  - Used in all functions which make API calls ([getRoomToken]{@link ApiService#getRoomToken}, [getRoomDetails]{@link ApiService#getRoomDetails}, [getRoomConfig]{@link ApiService#getRoomConfig}, [updateParticipant]{@link ApiService#updateParticipant})
     * - If the token is set, the headers will include the token
     * @returns HttpHeaders
     */
    get headers(): HttpHeaders {
        let headers: HttpHeaders;

        if (this.token) {
            headers = new HttpHeaders({
                'Content-Type': 'application/json',
                TOKEN: this.token,
            });
        } else {
            headers = new HttpHeaders({
                'Content-Type': 'application/json',
            });
        }

        return headers;
    }

    constructor(private http: HttpClient) {}

    /**
     * Gets the token for the room
     * @param roomId {string} The room ID
     * @returns Observable - subscribed to in [getRoomToken()]{@link WelcomeComponent#getRoomToken} to set the [token]{@link ApiService#token} getter in the ApiService
     */
    getRoomToken(roomId: string, params?: any) {
        return this.http.get(
            this.apiBaseUrl + 'talk/interview-room/exchange/' + roomId,
            params && Object.keys(params).length > 0
                ? { headers: this.headers, params }
                : { headers: this.headers },
        );
    }

    /**
     * Gets the details for the room
     * @returns Observable - subscribed to in [getRoomToken()]{@link WelcomeComponent#getRoomToken} to set the [roomData]{@link RoomService#roomData} in the RoomService, and call [updateRoleSelected()]{@link UserService#updateRoleSelected} in the UserService
     */
    getRoomDetails(params?: any) {
        let options = params
            ? {
                  headers: this.headers,
                  params,
              }
            : {
                  headers: this.headers,
              };

        return this.http.get(this.apiBaseUrl + 'talk/interview-room', options);
    }

    /**
     * Gets the details for the room with seperate token
     * This is for the AI Moderator to let the moderator join as a participant
     */
    getRoomDetailsWithToken(token: string) {
        return this.http.get(this.apiBaseUrl + 'talk/interview-room', {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                TOKEN: token,
            }),
        });
    }

    /**
     * Gets the configuration for the room
     *  - Returned in [getRoomConfig()]{@link StreamProviderService#getRoomConfig}
     * @returns Observable - subscribed to in [joinRoom()]{@link WelcomeComponent#joinRoom}
     * @returns Observable - subscribed to in [joinRoom()]{@link SetupComponent#joinRoom}
     */
    getRoomConfig() {
        return this.http.get(this.apiBaseUrl + 'talk/interview-room/config', {
            headers: this.headers,
        });
    }

    /**
     * Updates the participant
     * @param data {any} The data to update the participant with
     * @returns Observable - subscribed to in [addParticipant()]{@link ParticipantService#addParticipant}
     */
    updateParticipant(data: any) {
        return this.http.put(this.apiBaseUrl + 'talk/interview-room', data, {
            headers: this.headers,
        });
    }

    getTaskDetails(questionId: string, userData: any) {
        return this.http.post(
            this.apiBaseUrl + 'talk/interview-room/task/' + questionId,
            { user_data: userData },
            {
                headers: this.headers,
            },
        );
    }

    decodeAdditionalData(additionalData: string): any {
        // Non-string input can't be decoded — return an empty object so callers
        // that immediately read/assign properties don't throw.
        if (typeof additionalData !== 'string') {
            return {};
        }

        // An empty/blank string means "no additional data" (e.g. a direct,
        // non-embedded join) — not corruption. JSON.parse('') throws
        // "Unexpected end of JSON input", so short-circuit to avoid a spurious
        // error log on a legitimate entry path.
        if (additionalData.trim() === '') {
            return {};
        }

        // Decode behaviour is unchanged; it is only wrapped so that malformed
        // percent-encoding (decodeURIComponent -> URIError) or malformed JSON
        // (JSON.parse -> SyntaxError) can't throw synchronously and blank the
        // screen at interview entry. On failure we return a safe empty object.
        try {
            while (additionalData.includes('%2522')) {
                additionalData = decodeURIComponent(additionalData);
            }

            while (additionalData.includes('%22')) {
                additionalData = decodeURIComponent(additionalData);
            }

            while (typeof additionalData === 'string') {
                additionalData = JSON.parse(additionalData);
            }
        } catch (e) {
            console.error('Failed to decode additionalData', e);
            return {};
        }

        // The parse loop above can legitimately terminate on a non-object
        // (e.g. a number/null), so guard the property access.
        if (
            additionalData &&
            typeof additionalData === 'object' &&
            (additionalData as any)['Question asked']
        ) {
            delete (additionalData as any)['Question asked'];
        }

        return additionalData;
    }

    quotaCheck(identifier: string, userData: any) {
        const additionalData: any = this.decodeAdditionalData(userData);

        /*  @TODO - This will move to the limesurvey at some point:*/
        additionalData['device_type'] = 'desktop';

        return this.http.get(
            this.apiBaseUrl +
                `question/quota/${identifier}?additional_data=${encodeURIComponent(JSON.stringify(additionalData))}`,
        );
    }

    /**
     * Posts a new insight
     * @param data {any} The data to add the insight - This includes the type (tag), text, and start time
     * @returns Observable - subscribed to in [createInsight()]{@link ChatService#createInsight}
     */
    addInsight(data: any) {
        return this.http.post(
            this.apiBaseUrl + 'talk/interview-room/tag',
            data,
            {
                headers: this.headers,
            },
        );
    }

    createSpilloverRoom(userData: string) {
        return this.http.post(
            this.apiBaseUrl + 'talk/interview-room/spillover',
            {
                additional_data: userData,
            },
            {
                headers: this.headers,
            },
        );
    }

    sendTranscript(
        recordingId: string,
        transcript: string,
        name: string,
        discussionGuide: string,
        conversationId: string | null,
        userId: string,
        completedTopics?: any[],
        transcriptTimestamp?: string,
        videoAnalytics?: any,
        feedback?: any[],
        quantResponses?: any[],
        audioAnalytics?: any,
    ) {
        return this.http.post(
            this.apiBaseUrl + 'talk/interview-room/recording',
            this.buildTranscriptRequestBody(
                recordingId,
                transcript,
                name,
                discussionGuide,
                conversationId,
                userId,
                completedTopics,
                transcriptTimestamp,
                videoAnalytics,
                feedback,
                quantResponses,
                audioAnalytics,
            ),
            {
                headers: this.headers,
            },
        );
    }

    /**
     * Same payload as [sendTranscript]{@link ApiService#sendTranscript}, but
     * posted via fetch with keepalive so the request survives page unload —
     * browsers cancel in-flight XHR when the page is torn down, which silently
     * lost the transcript whenever a participant closed the tab mid-interview.
     */
    sendTranscriptKeepalive(
        recordingId: string,
        transcript: string,
        name: string,
        discussionGuide: string,
        conversationId: string | null,
        userId: string,
        completedTopics?: any[],
        transcriptTimestamp?: string,
        videoAnalytics?: any,
        feedback?: any[],
        quantResponses?: any[],
        audioAnalytics?: any,
    ): void {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };
        if (this.token) {
            headers['TOKEN'] = this.token;
        }

        try {
            fetch(this.apiBaseUrl + 'talk/interview-room/recording', {
                method: 'POST',
                keepalive: true,
                headers,
                body: JSON.stringify(
                    this.buildTranscriptRequestBody(
                        recordingId,
                        transcript,
                        name,
                        discussionGuide,
                        conversationId,
                        userId,
                        completedTopics,
                        transcriptTimestamp,
                        videoAnalytics,
                        feedback,
                        quantResponses,
                        audioAnalytics,
                    ),
                ),
            }).catch((error) =>
                console.error('Keepalive transcript send failed', error),
            );
        } catch (error) {
            console.error('Keepalive transcript send failed', error);
        }
    }

    private buildTranscriptRequestBody(
        recordingId: string,
        transcript: string,
        name: string,
        discussionGuide: string,
        conversationId: string | null,
        userId: string,
        completedTopics?: any[],
        transcriptTimestamp?: string,
        videoAnalytics?: any,
        feedback?: any[],
        quantResponses?: any[],
        audioAnalytics?: any,
    ) {
        return {
            recording_id: recordingId,
            transcript,
            transcript_timestamp: transcriptTimestamp,
            ai_moderator: true,
            additional_data: {
                // Quant answers are spread first so a question whose text
                // collides with a reserved key below can never overwrite the
                // platform's own metadata.
                ...this.buildQuantAdditionalData(quantResponses),
                ELConversationId: conversationId,
                name: name,
                moderator: 'AI Moderator',
                vpm_discussion_guide: this.encodeToBase64(discussionGuide),
                vpm_user_id: userId,
                vpm_completed_topics:
                    this.encodeToBase64(JSON.stringify(completedTopics)) || [],
                vpm_video_analytics: videoAnalytics
                    ? this.encodeToBase64(JSON.stringify(videoAnalytics))
                    : null,
                vpm_audio_analytics: audioAnalytics
                    ? this.encodeToBase64(JSON.stringify(audioAnalytics))
                    : null,
                vpm_feedback: feedback
                    ? this.encodeToBase64(JSON.stringify(feedback))
                    : null,
            },
        };
    }

    /**
     * One additional_data key per quant question — the key is the question
     * text, the value is the answer. The platform stores additional_data as
     * generic per-response attributes, so each question lands as its own
     * attribute rather than inside an opaque blob.
     *
     * Multi-select answers are joined for readability; format, topic title and
     * timestamp are deliberately not sent. Two topics asking the same question
     * therefore share one key and the later answer wins.
     */
    private buildQuantAdditionalData(
        quantResponses?: any[],
    ): Record<string, string | number> {
        if (!quantResponses?.length) {
            return {};
        }

        return quantResponses.reduce(
            (acc: Record<string, string | number>, response: any) => {
                if (!response?.question) {
                    return acc;
                }
                acc[response.question] = Array.isArray(response.value)
                    ? response.value.join(', ')
                    : response.value;
                return acc;
            },
            {} as Record<string, string | number>,
        );
    }

    /**
     * Safely encodes a string to base64, handling Unicode characters
     * @param str String to encode to base64
     * @returns Base64 encoded string
     */
    private encodeToBase64(str: string): string {
        try {
            // For browsers that support TextEncoder
            return btoa(
                new TextEncoder()
                    .encode(str)
                    .reduce(
                        (data, byte) => data + String.fromCharCode(byte),
                        '',
                    ),
            );
        } catch (e) {
            // Fallback for browsers without TextEncoder
            return btoa(unescape(encodeURIComponent(str)));
        }
    }

    validateUserByToken() {
        return this.http.post(
            this.apiBaseUrl + 'talk/interview-room/validate-user-by-token',
            { strict: true },
            {
                headers: this.headers,
            },
        );
    }

    updateUserData(isOwnSample: boolean, additionalData: any) {
        let url = this.apiBaseUrl + 'agile-qual/survey/';

        additionalData = this.decodeAdditionalData(additionalData);

        if (isOwnSample) {
            url += `screen/in?response_id=${additionalData.vpm_user_id}&survey=${additionalData.vpm_sid}`;
        } else {
            url += `embed/in?pp_id=161&respondent_id=${additionalData.vpm_user_id}&survey=${additionalData.vpm_sid}`;
        }

        return this.http.get(url, {
            headers: this.headers,
        });
    }

    checkIsDuplicateUser(additionalData: any) {
        try {
            additionalData = this.decodeAdditionalData(additionalData);

            return this.http.post(
                this.apiBaseUrl + 'talk/interview-room/validate-user',
                additionalData,
                {
                    headers: this.headers,
                },
            );
        } catch (error) {
            console.error('Error in checkIsDuplicateUser:', error);
            throw error;
        }
    }

    submitFeedback(feedBackData: any) {
        return this.http.post(
            this.apiBaseUrl + 'talk/interview-room/feedback',
            feedBackData,
            {
                headers: this.headers,
            },
        );
    }

    getCaptionToken() {
        return this.http.get(
            this.apiBaseUrl + 'talk/interview-room/caption-token',
            {
                headers: this.headers,
            },
        );
    }

    translateCaption(data: {
        text: string;
        source_language: string;
        target_language: string;
    }) {
        return this.http.post(
            this.apiBaseUrl + 'talk/interview-room/translate',
            data,
            {
                headers: this.headers,
            },
        );
    }
}
